import { lazy, Suspense } from 'react'
import type { CropperRef } from 'react-advanced-cropper'
import { FileInput, Spinner } from '../../atoms'
import { useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import { nanoid } from 'nanoid'
import { dataUrlToFile, debounce, readFile } from '@/utils'
import { FormattedMessage, useIntl } from 'react-intl'
import { strings } from './Cropper.strings'
import { useLayoutInfo } from '@/ui/core'

// Lazy load the actual cropper to avoid loading the heavy library upfront
const LazyRACropper = lazy(() =>
  import('react-advanced-cropper').then((module) => {
    // Also import the CSS files when the component is loaded
    import('react-advanced-cropper/dist/style.css')
    import('react-advanced-cropper/dist/themes/corners.css')
    return { default: module.Cropper }
  }),
)

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
`

const FileInputContainer = styled.div<{
  $horizontalPosition?: 'left' | 'right' | 'center'
}>`
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: ${({ $horizontalPosition }) =>
    $horizontalPosition === 'center'
      ? 'center'
      : $horizontalPosition === 'right'
        ? 'flex-end'
        : 'flex-start'};
`

const CropperWrapper = styled.div<{ $isMobile?: boolean }>`
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      touch-action: none; /* Prevents unwanted scrolling on mobile while using the cropper */
    `}
`

// Create a styled version of the lazy cropper
const StyledLazyCropper = styled(LazyRACropper)<{
  $width?: string
  $isMobile?: boolean
}>`
  width: ${({ $width }) => ($width ? $width : '100%')};
  max-width: 100%;
  height: auto;
  aspect-ratio: ${({ $isMobile }) => ($isMobile ? '4/3' : '16/9')};

  /* Improve touch handling */
  & .react-advanced-cropper__background {
    touch-action: none;
  }

  /* Ensure handles are large enough for touch */
  & .react-advanced-cropper__handles-container {
    --size: 24px;
    @media (max-width: 768px) {
      --size: 32px; /* Larger handles on mobile */
    }
  }
`

interface Props {
  aspect?: number
  className?: string
  label?: string
  onChange?: (value?: File) => void
  required?: boolean
  width?: string
  horizontalButtonPosition?: 'left' | 'right' | 'center'
  verticalButtonPosition?: 'top' | 'bottom'
}

const LoadingContainer = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #ccc;
  border-radius: 8px;
  color: #666;
  font-size: 16px;
`

const CropperLoading = () => (
  <LoadingContainer>
    <Spinner />
  </LoadingContainer>
)

const PlaceholderContainer = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  height: 100%;
  aspect-ratio: ${({ $isMobile }) => ($isMobile ? '4/3' : '16/9')};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #ccc;
  border-radius: 8px;
  color: #999;
  font-size: 16px;
  background-color: #fafafa;
`

// Add a placeholder for when no image is selected
const CropperPlaceholder = ({ isMobile }: { isMobile: boolean }) => (
  <PlaceholderContainer $isMobile={isMobile}>
    <FormattedMessage {...strings.share} />
  </PlaceholderContainer>
)

export const Cropper: React.FC<Props> = ({
  aspect = 5 / 4,
  className,
  label,
  onChange,
  required,
  width,
  horizontalButtonPosition = 'center',
  verticalButtonPosition = 'bottom',
}) => {
  const intl = useIntl()
  const [image, setImage] = useState<string | undefined>(undefined)
  const [file, setFile] = useState<File | undefined>(undefined)
  const { size } = useLayoutInfo()
  const isMobile = size === 'narrow'

  const getImage = useCallback(
    async (files: FileList | null) => {
      if (!files) return
      if (files.length < 0) return
      const selected = files[0]
      setFile(selected)
      const result = (await readFile(selected)) as string
      setImage(result)
      onChange?.(file)
    },
    [setImage, onChange, file],
  )

  const onCropChange = debounce((cropper: CropperRef) => {
    const cropped = cropper.getCanvas()?.toDataURL()
    if (!cropped) return
    const fileName = file?.name ?? nanoid(10)
    const result = dataUrlToFile(cropped, fileName)
    onChange?.(result)
  }, 500)

  return (
    <Container>
      {verticalButtonPosition === 'top' && (
        <FileInputContainer $horizontalPosition={horizontalButtonPosition}>
          <FileInput
            data-testid="Cropper__FileInput"
            label={label ? label : intl.formatMessage(strings.upload)}
            onChange={(e) => getImage(e.target.files)}
            required={required}
          />
        </FileInputContainer>
      )}
      <CropperWrapper data-testid="Test__Cropper" $isMobile={isMobile}>
        {image ? (
          <Suspense fallback={<CropperLoading />}>
            <StyledLazyCropper
              className={className}
              src={image}
              onChange={onCropChange}
              stencilProps={{
                aspectRatio: aspect,
                grid: true,
              }}
              $width={width}
              $isMobile={isMobile}
              checkOrientation={true}
            />
          </Suspense>
        ) : (
          <CropperPlaceholder isMobile={isMobile} />
        )}
      </CropperWrapper>
      {verticalButtonPosition === 'bottom' && (
        <FileInputContainer $horizontalPosition={horizontalButtonPosition}>
          <FileInput
            data-testid="Cropper__FileInput"
            label={label ? label : intl.formatMessage(strings.upload)}
            onChange={(e) => getImage(e.target.files)}
            required={required}
          />
        </FileInputContainer>
      )}
    </Container>
  )
}
